import React from 'react';
import {
    User,
    CalendarDays,
    Zap,
    ShieldCheck,
    AlertTriangle,
    XOctagon,
    LucideTicketX,
    LucideTicketCheck,
    CalendarArrowUp,
    BatteryPlus,
    FileText,
    MessageCircle,
    BookOpen,
    HelpCircle,
    ClipboardList,
    Activity
} from 'lucide-react';
import { useUMAStats } from './UMAStatsContext';

const VoucherStatus = ({
    expiration,
    autoRenew,
    statusCode,
    voucherType,
    username,
    userId,
    signup_date,
    last_login,
    file_health,
    TicketCheckIcon,
    TicketXIcon,
    user_counts,
    system_counts,
    onShowFuelModal
}) => {
    const { umaSize, recalculating } = useUMAStats();

    if (!expiration) {
        return <p className="text-red-500 font-mono">No voucher data available.</p>;
    }

    const now = new Date();
    const exp = new Date(expiration);
    const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));

    let status = '';
    if (!autoRenew) {
        status = 'Reactivation Required';
    } else if (daysLeft <= 5) {
        status = '? – Renewal Pending';
    } else {
        status = 'Renewal – Free';
    }

    const statusLabels = {
        T0: "✅ Access Stable",
        T1: "⚠️ Drift Detected",
        T2: "⚠️ Mnemolysis Risk",
        TX: "❌ Access Lost – Memory Status = Toxic Goo Confirmed"
    };

    const typeLabels = {
        1: "System",
        2: "Restored",
        3: "Restricted",
        4: "Purchased"
    };

    const iconClass = "text-white opacity-80";

    // Format signup and last login dates
    const signupDateStr = signup_date ? new Date(signup_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Unknown';
    const lastLoginStr = last_login ? new Date(last_login).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

    // File Health Bar Color
    const health = Number(file_health);
    let healthColor = '#22c55e'; // green
    if (health > 100) healthColor = '#3b82f6'; // blue
    else if (health > 70) healthColor = '#22c55e'; // green
    else if (health > 40) healthColor = '#eab308'; // yellow
    else if (health > 20) healthColor = '#f97316'; // orange
    else healthColor = '#ef4444'; // red

    // Format UMA file size safely
    let fileSizeDisplay = 'Loading...';
    if (typeof umaSize === 'number' && !isNaN(umaSize)) {
        fileSizeDisplay = `${(umaSize / 1024).toFixed(1)} KB`;
    } else if (umaSize === 0) {
        fileSizeDisplay = '0 KB';
    }

    return (
        <div className="bg-[#0a0a0a] text-green-300 border border-green-500 p-6 rounded-xl shadow-lg font-mono w-full max-w-xl text-sm sm:text-base space-y-2 leading-snug tracking-wide">
            {/* 1. User */}
            <p className="flex items-center gap-2">
                <User size={14} className="text-purple-300" />
                <strong>User:</strong> {username} — UMA:|:SN_{String(userId).padStart(4, '0')}
            </p>
            {/* 2. Registered */}
            <p className="flex items-center gap-2">
                <CalendarArrowUp size={14} className={iconClass} />
                <strong>Registered:</strong> {signupDateStr}
            </p>
            {/* 3. Last Login */}
            <p className="flex items-center gap-2">
                <CalendarDays size={14} className={iconClass} />
                <strong>Last Login:</strong> {lastLoginStr || <span className="italic text-yellow-400">Previous login: N/A</span>}
            </p>
            {/* 4. Voucher */}
            <p className="flex items-center gap-2">
                <LucideTicketCheck size={14} className="text-green-300" />
                <strong>Voucher:</strong> {typeLabels[voucherType] || 'Unknown'}
            </p>
            {/* 5. Expires */}
            <p className="flex items-center gap-2">
                <CalendarDays size={14} className="text-yellow-300" />
                <strong>Expires:</strong> {exp.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })} ({daysLeft} days left)
            </p>
            {/* 6. Integrity */}
            <p className="flex items-center gap-2">
                {statusCode === 'T0' && <ShieldCheck size={14} className="text-blue-300" />}
                {statusCode === 'T1' && <AlertTriangle size={14} className="text-yellow-400" />}
                {statusCode === 'T2' && <AlertTriangle size={14} className="text-orange-500" />}
                {statusCode === 'TX' && <XOctagon size={14} className="text-red-500" />}
                <strong>Integrity:</strong> {statusLabels[statusCode] || 'Unknown'}
            </p>
            {/* 7. Status */}
            <p className="flex items-center gap-2">
                <Zap size={14} className="text-yellow-300" />
                <strong>Status:</strong> {status}
            </p>
            {/* 8. File Size */}
            <p className="flex items-center gap-2">
                <FileText size={14} className="text-blue-300" />
                <strong>UMA File Size:</strong> {fileSizeDisplay}
                {recalculating && <span className="ml-2 animate-spin">⌛</span>}
            </p>
            {/* 9. Count Table with ClipboardList icon and new label */}
            <div className="my-4">
                <div className="flex items-center gap-2 mb-2">
                    <ClipboardList size={16} className="text-white" />
                    <span className="font-bold text-green-300 text-base sm:text-base">Local Storage Node</span>
                </div>
                <table className="w-full text-xs border-collapse border-green-700">
                    <thead>
                        <tr className="text-green-300 border-b border-green-700">
                            <th className="py-1 px-2"></th>
                            <th className="py-1 px-2 text-center">
                                <span className="inline-flex items-center gap-1 justify-center">
                                    <MessageCircle size={12} className="inline-block align-middle" /> Chats
                                </span>
                            </th>
                            <th className="py-1 px-2 text-center">
                                <span className="inline-flex items-center gap-1 justify-center">
                                    <FileText size={12} className="inline-block align-middle" /> Documents
                                </span>
                            </th>
                            <th className="py-1 px-2 text-center">
                                <span className="inline-flex items-center gap-1 justify-center">
                                    <BookOpen size={12} className="inline-block align-middle" /> Summaries
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-green-800">
                            <td className="py-1 px-2 text-green-300 font-bold">UMA File</td>
                            <td className="py-1 px-2 text-center">{user_counts.chats}</td>
                            <td className="py-1 px-2 text-center">{user_counts.documents}</td>
                            <td className="py-1 px-2 text-center">{user_counts.summaries}</td>
                        </tr>
                        <tr>
                            <td className="py-1 px-2 text-green-300 font-bold">Core Module</td>
                            <td className="py-1 px-2 text-center">{system_counts.chats}</td>
                            <td className="py-1 px-2 text-center">{system_counts.documents}</td>
                            <td className="py-1 px-2 text-center">{system_counts.summaries}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {/* 10. Connection Health with HelpCircle icon */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                    <span className="flex items-center gap-2 font-bold">
                        <BatteryPlus size={16} className="text-white" />
                        UMA File Connection Health
                        <HelpCircle
                            size={16}
                            className="ml-2 text-green-400 cursor-pointer hover:text-yellow-300"
                            onClick={onShowFuelModal}
                            title="What is this? Click to learn more and support the station."
                        />
                    </span>
                    <span className="font-mono">{health}%</span>
                </div>
                <div className="w-full bg-gray-800 rounded h-3">
                    <div
                        className="h-3 rounded transition-all duration-500"
                        style={{
                            width: `${Math.min(health, 100)}%`,
                            background: healthColor
                        }}
                    />
                </div>
                {health > 100 && (
                    <div className="text-blue-400 text-xs mt-1 font-bold">Supercharged!</div>
                )}
            </div>
            {/* 11. File Health */}
            <p className="flex items-center gap-2">
                <Activity size={14} style={{ color: healthColor }} />
                <strong>UMA File Connection Health:</strong>
                <span className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden ml-2">
                    <div
                        className="h-full transition-all duration-500"
                        style={{
                            width: `${Math.min(100, health)}%`,
                            backgroundColor: healthColor
                        }}
                    />
                </span>
                <span style={{ color: healthColor }}>{health}%</span>
            </p>
        </div>
    );
};

export default VoucherStatus;